import { INDUSTRIES } from '@/landing/content'
import { ContentPage, contentMeta, type ContentPageData } from '@/site/content'

const sample = INDUSTRIES.find((i) => i.key === 'food')!

const page: ContentPageData = {
  path: '/dlya/restoraniv-i-dostavky',
  parent: { name: 'Галузі', path: '/dlya' },
  crumb: 'Ресторани й доставка',
  title: 'AI-адміністратор для ресторану та доставки | Anytel',
  description:
    'AI-агент відповідає на дзвінки ресторану й служби доставки: бронює столики, приймає замовлення, розповідає про меню й зону доставки. 24/7, українською.',
  eyebrow: 'Для ресторанів, кафе і доставки',
  h1: 'AI-адміністратор для ресторану та служби доставки',
  lead:
    'У п’ятницю ввечері телефон дзвонить саме тоді, коли зала повна, а кухня не встигає. AI-агент приймає бронювання й замовлення, відповідає про меню і години роботи, і жоден гість не чекає на лінії.',
  sections: [
    {
      h2: 'Що відбувається з дзвінками в пікові години',
      blocks: [
        'Ресторанний телефон живе піками: обід, вечір п’ятниці, свята. Хостес чи менеджер зали в цей час зустрічає гостей, і дзвінок або пропускають, або обривають на півслові. Службі доставки ще складніше: кожен пропущений дзвінок означає замовлення, яке за два кліки піде до конкурента.',
        {
          list: [
            'Гість хоче забронювати стіл на вечір, а лінія зайнята.',
            'Ті самі питання по колу: до котрої працюєте, чи є веганське меню, чи везете на Оболонь.',
            'Замовлення телефоном записують на папірці, і частина деталей губиться дорогою на кухню.',
          ],
        },
      ],
    },
    {
      h2: 'Що робить AI-агент',
      blocks: [
        { h3: 'Бронює столики' },
        'Агент знає години роботи і бачить вільний час у календарі бронювань. Столи чи зали можна завести окремими календарями. Агент пропонує час, уточнює побажання (дитячий стілець, тиха зала) і записує. Гість отримує підтвердження в розмові, менеджер отримує сповіщення.',
        { h3: 'Приймає замовлення на доставку' },
        'Що замовити, куди везти, на коли, як зручно оплатити. Агент збирає це в заявку і передає менеджеру в Telegram або у вашу систему доставки через API. Страви й ціни він бере з бази знань і не пропонує того, чого немає в меню.',
        { h3: 'Відповідає на часті питання' },
        'Меню, алергени, години роботи, зона і вартість доставки, банкети, парковка. Ви один раз описуєте це текстом, і агент відповідає однаково точно о другій дня й о другій ночі.',
        { h3: 'Не тримає гостя на лінії' },
        'Агент веде кілька розмов одночасно, тож у пікові години кожен, хто дзвонить, одразу чує відповідь, а не гудки. Скільки дзвінків одночасно, залежить від тарифу.',
      ],
    },
  ],
  dialog: {
    h2: 'Приклад розмови',
    context: 'Ресторан, субота, 18:05',
    dialog: sample.dialog,
    result: sample.result,
  },
  after: [
    {
      h2: 'Бронювання і замовлення одразу в команди',
      blocks: [
        'Кожне бронювання з’являється в календарі, кожне замовлення стає заявкою в кабінеті. Менеджер отримує сповіщення [у Telegram](/funktsii/zayavky-v-telegram) і бачить усе одразу: ім’я, телефон, час, кількість гостей чи склад замовлення.',
        'Гостям, які пишуть, а не дзвонять, той самий агент відповідає в Telegram, Viber, Instagram і Messenger, з тими самими знаннями про меню.',
      ],
    },
    {
      h2: 'Як підключити ресторан',
      blocks: [
        {
          steps: [
            { title: 'Меню і правила', text: 'Додайте в базу знань меню з цінами, години роботи, зону доставки й правила бронювання.' },
            { title: 'Столи і зали', text: 'Створіть календарі для зал чи столів, якщо агент має бронювати сам. Або нехай він приймає заявки на бронювання, а підтверджує менеджер.' },
            { title: 'Тестовий дзвінок і номер', text: 'Подзвоніть агенту з браузера, а потім увімкніть переадресацію: на весь час або тільки коли лінія зайнята.' },
          ],
        },
      ],
    },
  ],
  faq: [
    {
      q: 'Агент зможе прийняти замовлення з кількома стравами?',
      a: 'Так. Він уточнює кожну позицію й кількість, повторює замовлення наприкінці і передає його менеджеру. Страви й ціни бере з вашого меню в базі знань.',
    },
    {
      q: 'Що з оплатою?',
      a: 'Агент не приймає оплату. Він може розповісти, як оплатити (готівкою кур’єру, карткою, за посиланням), а сам платіж проходить так, як у вас заведено.',
    },
    {
      q: 'Чи можна відповідати і в месенджерах?',
      a: 'Так, той самий агент працює в Telegram, Viber, Instagram і Messenger. Усі розмови зібрані в одному розділі кабінету.',
    },
    {
      q: 'А якщо гість хоче замовити банкет?',
      a: 'Такі запити краще передавати людині. Агент збере основне: дату, кількість гостей, побажання і контакт, і передасть заявку менеджеру.',
    },
  ],
  related: ['/funktsii/zayavky-v-telegram', '/funktsii/robota-24-7', '/funktsii/ai-agent-dlya-dzvinkiv', '/dlya/saloniv-krasy'],
}

export const meta = contentMeta(page)

export default function Page() {
  return <ContentPage page={page} />
}
